import { DOCUMENT } from "@angular/common";
import { inject, Injectable, Signal, signal } from "@angular/core";

type Theme = "light" | "dark";

/**
 * @summary Service to manage and toggle between light and dark themes.
 * @description Provides functionality to detect the system's preferred color scheme, apply themes, and toggle between them.
 */
@Injectable({
  providedIn: "root",
})
export class ThemeService {
  /**
   * @summary Holds the current theme as a reactive signal.
   * @description The initial value is set to 'dark' by default. This signal can be subscribed to for theme changes.
   */
  private _theme = signal<Theme>("dark");
  document = inject(DOCUMENT);

  /**
   * @summary Provides a read-only reactive signal of the current theme.
   * @description Used to observe changes in the theme.
   * @returns A Signal of the current theme.
   */
  get theme(): Signal<Theme> {
    return this._theme;
  }

  constructor() {
    this.detectTheme();
  }

  /**
   * @summary Updates the current theme and applies it to the document.
   * @description This sets the theme and updates the `data-theme` attribute on the root element.
   * @param theme The new theme to be applied ('light' or 'dark').
   */
  setTheme(theme: Theme) {
    this._theme.set(theme);
    this.document.documentElement.setAttribute("data-theme", theme);
  }

  /**
   * @summary Toggles between light and dark themes.
   * @description Switches the theme based on the current theme value.
   */
  toggleTheme(): void {
    const newTheme = this._theme() === "light" ? "dark" : "light";
    this.setTheme(newTheme);
  }

  /**
   * @summary Detects the system's preferred color scheme and applies it as the theme.
   * @description Uses the `prefers-color-scheme` media query to determine the initial theme and applies it.
   */
  detectTheme(): void {
    const preferredTheme = "light";
    this.setTheme(preferredTheme);
  }
}
